/**
 * "Por que a classificação é esta" (2026-10-20, decisão da dona: nada de
 * JSON na tela, uma lista legível por dimensão).
 *
 * Duas camadas na tela, e este arquivo é a de baixo de cada uma:
 *
 * - **Permanente**: `classificationWhy` monta, a partir da PRÓPRIA menção
 *   (sem requisição), uma seção por dimensão — polaridade, abordagem,
 *   temas e mensagens-chave — com a origem de cada valor e a justificativa
 *   curta que o servidor guarda no Postgres. Sempre aparece.
 * - **Rastro (Mongo, 60 dias)**: `traceThemes` e os leitores vizinhos
 *   resumem o `parsed_result` da IA. Rastro gravado antes de 2026-10-20
 *   não tem `origin`/`detail`/`rule`/`matched_terms`; todo leitor aqui
 *   tolera campo ausente.
 *
 * Tudo puro e testado; o componente só renderiza o que sai daqui.
 *
 * **Origem `null` não é "do motor"**: é histórico anterior ao registro de
 * origem (sem backfill) ou valor posto à mão — a correção manual zera a
 * origem e a justificativa da dimensão que mudou. Quem separa os dois,
 * pra polaridade e abordagem, é `classificationMethod === "manual"`.
 */

import type { CatalogRef, Mention } from "@/shared/types/mention";
import { sourceLabel, themeSourceFullLabel } from "@/shared/vocabulary/classification-sources";
import type { PremiseFor } from "./use-premise-texts";

export type WhyDimension = "polarity" | "approach" | "themes" | "key_messages";

export type WhyLine = {
  key: string;
  /** O que está sendo explicado: o rótulo da polaridade, o nome do tema… */
  label: string;
  /** Por que é assim. `null` quando não há o que explicar (linha vazia). */
  reason: string | null;
  /** Linhas de apoio abaixo da principal (a participação por entidade). */
  details: string[];
  /** Tema em destaque: escolhido por alguém ou sugestão automática. */
  star: "manual" | "auto" | null;
  /** A origem do valor: a crua do servidor (`term`, `ai`, `imported`,
   *  `publisher`, `vehicle`), `manual` quando a polaridade ou a abordagem
   *  foi posta à mão, ou `null` sem registro (e nas linhas vazias). */
  source: string | null;
  /** O selo curto da origem ("termo cadastrado", "IA · premissa nº 27",
   *  "regra de cadastro: veículo"…) — a página das justificativas o põe
   *  antes do texto. `null` nas linhas vazias ("Sem polaridade"). */
  badge: string | null;
  /** A justificativa PURA, sem o prefixo da origem: os termos que casaram,
   *  a sub-expressão do tema, a frase da IA. `null` quando só há origem. */
  text: string | null;
  /** Uma nota ao lado da justificativa, que NÃO é pesquisável pelo `why_q`
   *  do servidor (ele só olha as colunas de justificativa): hoje, o texto
   *  da premissa que a IA citou. A tela a mostra sem marcar a busca, pra
   *  não parecer que a busca a alcança. `null` quando não há. */
  note: string | null;
};

/** Uma linha sem valor ("Sem polaridade", "Nenhum tema"): nada a explicar. */
function emptyLine(key: string, label: string): WhyLine {
  return { key, label, reason: null, details: [], star: null, source: null, badge: null, text: null, note: null };
}

const POSITION_SUFFIX = (position: number | null | undefined) =>
  position === null || position === undefined ? "" : ` · premissa nº ${position}`;

/** A origem + selo de polaridade e abordagem. */
function singleValueOrigin(
  source: string | null,
  manual: boolean,
  position: number | null
): { source: string | null; badge: string } {
  if (source === "term") return { source, badge: "termo cadastrado" };
  if (source === "ai") return { source, badge: `IA${POSITION_SUFFIX(position)}` };
  if (source === "imported") return { source, badge: "importada da planilha" };
  return manual
    ? { source: "manual", badge: "corrigida à mão" }
    : { source: null, badge: "sem registro" };
}

const THEME_BADGE: Record<string, string> = {
  term: "termo cadastrado",
  ai: "IA",
  imported: "importado da planilha",
  publisher: "regra de cadastro: publicador",
  vehicle: "regra de cadastro: veículo"
};

const MESSAGE_BADGE: Record<string, string> = {
  term: "termo cadastrado",
  ai: "IA",
  imported: "importada da planilha"
};

export type WhySection = {
  key: WhyDimension;
  title: string;
  lines: WhyLine[];
};

/** "Rótulo — razão", a frase como a tela a mostra. */
export function whyLineText(line: WhyLine): string {
  return line.reason ? `${line.label} — ${line.reason}` : line.label;
}

const SEPARATOR_TERMS = /\s*;\s*/;
const SEPARATOR_PARTICIPATION = /\s*\|\s*/;

function splitNonEmpty(text: string | null | undefined, separator: RegExp): string[] {
  return (text ?? "").split(separator).map(part => part.trim()).filter(Boolean);
}

function quoted(terms: string[]): string {
  return terms.map(term => `"${term}"`).join("; ");
}

/** A razão de uma dimensão sem lista (polaridade e abordagem), pela
 *  origem. `ai` e `term` ganham o complemento que o servidor mandou. */
function singleValueReason(
  source: string | null,
  manual: boolean,
  complement: { term?: string; ai?: string }
): string {
  if (source === "term") return complement.term ?? "pelo termo cadastrado";
  if (source === "ai") return complement.ai ?? "pela IA";
  const label = sourceLabel(source);
  if (label) return label;
  return manual ? "corrigida à mão" : "sem registro de origem";
}

function polaritySection(mention: Mention, premiseFor?: PremiseFor): WhySection {
  const manual = mention.classificationMethod === "manual";
  if (mention.polarity === null) {
    return {
      key: "polarity",
      title: "Polaridade",
      lines: [emptyLine("polarity", "Sem polaridade")]
    };
  }
  const terms = splitNonEmpty(mention.polarityJustification, SEPARATOR_TERMS);
  const position = mention.polarityRulePosition;
  // O número é sempre o global (o que a IA cita); o cadastro só acrescenta
  // o que a regra afirma e a seção a que pertence.
  const premise =
    mention.polaritySource === "ai" && position !== null && position !== undefined
      ? premiseFor?.(mention.brandId, mention.mediaType, position) ?? null
      : null;
  const reason = singleValueReason(mention.polaritySource, manual, {
    term: terms.length ? `termo cadastrado: ${quoted(terms)}` : undefined,
    ai:
      position !== null
        ? `pela IA, premissa nº ${position}${premise ? ` (${premise.sectionLabel}): ${premise.text}` : ""}`
        : undefined
  });
  const origin = singleValueOrigin(mention.polaritySource, manual, position);
  return {
    key: "polarity",
    title: "Polaridade",
    lines: [
      {
        key: "polarity",
        label: mention.sentiment,
        reason,
        details: [],
        star: null,
        ...origin,
        badge: premise ? `${origin.badge} · ${premise.sectionLabel}` : origin.badge,
        text: mention.polaritySource === "term" && terms.length ? quoted(terms) : null,
        note: premise ? premise.text : null
      }
    ]
  };
}

function approachSection(mention: Mention): WhySection {
  const manual = mention.classificationMethod === "manual";
  if (mention.approachValue === null) {
    return {
      key: "approach",
      title: "Abordagem",
      lines: [emptyLine("approach", "Sem abordagem")]
    };
  }
  // Abordagem por "termo" é a regra da marca no TÍTULO (decidida sem IA):
  // a frase que o motor escreveu já diz qual marca e se há outra junto.
  const byTitle = mention.approachSource === "term";
  const reason = singleValueReason(mention.approachSource, manual, {
    term: mention.approachJustification?.trim() || "marca no título"
  });
  // A participação por entidade é do papel que a IA deu; só quando a
  // origem é a IA ela explica alguma coisa.
  const details =
    mention.approachSource === "ai"
      ? splitNonEmpty(mention.approachJustification, SEPARATOR_PARTICIPATION)
      : [];
  return {
    key: "approach",
    title: "Abordagem",
    lines: [
      {
        key: "approach",
        label: mention.approach,
        reason,
        details,
        star: null,
        ...singleValueOrigin(mention.approachSource, manual, null),
        ...(byTitle ? { badge: "marca no título" } : {}),
        text: null,
        note: null
      }
    ]
  };
}

function themeReason(theme: CatalogRef): string {
  const justification = theme.justification?.trim() || null;
  if (theme.source === "term") {
    // A justificativa do tema é a SUB-EXPRESSÃO cadastrada que casou
    // (`"Fé no Brasil"`, `EPR AND (…)`), e ela já traz as próprias
    // aspas quando o termo é entre aspas — pôr outras por cima dava
    // `""Fé no Brasil""`. Os termos de POLARIDADE são nomes soltos, e
    // lá as aspas são nossas (`quoted`).
    return justification ? `termo cadastrado: ${justification}` : "termo cadastrado";
  }
  if (theme.source === "ai") {
    return justification ? `pela IA: ${justification}` : "pela IA";
  }
  return themeSourceFullLabel(theme.source) ?? "sem registro";
}

function themesSection(mention: Mention): WhySection {
  if (mention.themes.length === 0) {
    return {
      key: "themes",
      title: "Subtemas",
      lines: [emptyLine("themes", "Nenhum subtema")]
    };
  }
  // O destaque vem primeiro (o servidor já manda assim; a ordenação
  // estável garante o contrato mesmo se ele mudar).
  const ordered = [...mention.themes].sort(
    (a, b) => Number(Boolean(b.highlight)) - Number(Boolean(a.highlight))
  );
  return {
    key: "themes",
    title: "Subtemas",
    lines: ordered.map(theme => ({
      key: `theme-${theme.id}`,
      label: theme.group_name ? `${theme.group_name} · ${theme.name}` : theme.name,
      reason: themeReason(theme),
      details: [],
      star: theme.highlight ?? null,
      source: theme.source ?? null,
      badge: (theme.source && THEME_BADGE[theme.source]) || "sem registro",
      text: theme.justification?.trim() || null,
      note: null
    }))
  };
}

function keyMessagesSection(mention: Mention): WhySection {
  if (mention.keyMessages.length === 0) {
    return {
      key: "key_messages",
      title: "Mensagens-chave",
      lines: [emptyLine("key_messages", "Nenhuma mensagem-chave")]
    };
  }
  return {
    key: "key_messages",
    title: "Mensagens-chave",
    lines: mention.keyMessages.map(message => ({
      key: `message-${message.id}`,
      label: message.name,
      reason: sourceLabel(message.source) ?? "sem registro",
      details: [],
      star: null,
      source: message.source ?? null,
      badge: (message.source && MESSAGE_BADGE[message.source]) || "sem registro",
      text: null,
      note: null
    }))
  };
}

/** As quatro seções da camada permanente, sempre nesta ordem. */
export function classificationWhy(mention: Mention, premiseFor?: PremiseFor): WhySection[] {
  return [
    polaritySection(mention, premiseFor),
    approachSection(mention),
    themesSection(mention),
    keyMessagesSection(mention)
  ];
}

/* --- camada do rastro --------------------------------------------------- */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.map(asRecord).filter((item): item is Record<string, unknown> => item !== null)
    : [];
}

export type TraceThemeOrigins = { term: number; rule: number; ai: number };

export type TraceThemes = {
  /** Os nomes dos temas que o resultado da tarefa trouxe. */
  names: string[];
  /** `null` = rastro anterior à origem por tema (sem o campo `origin`). */
  origins: TraceThemeOrigins | null;
};

/** Os temas do `parsed_result` de uma tarefa de temas, com a contagem por
 *  origem quando o rastro a tem. */
export function traceThemes(parsed: Record<string, unknown> | null | undefined): TraceThemes {
  const themes = records(parsed?.themes);
  const names = themes.map(theme => theme.name).filter((n): n is string => typeof n === "string");
  const origins: TraceThemeOrigins = { term: 0, rule: 0, ai: 0 };
  let known = false;
  for (const theme of themes) {
    const origin = theme.origin;
    if (origin === "term" || origin === "rule" || origin === "ai") {
      origins[origin] += 1;
      known = true;
    }
  }
  return { names, origins: known ? origins : null };
}

/** "3 por termo cadastrado, 1 pela IA, 2 por regra de cadastro" — só as
 *  origens que aparecem. Vazio quando o rastro não tem origem. */
export function describeThemeOrigins(origins: TraceThemeOrigins | null): string {
  if (!origins) return "";
  const parts: string[] = [];
  if (origins.term) parts.push(`${origins.term} por termo cadastrado`);
  if (origins.ai) parts.push(`${origins.ai} pela IA`);
  if (origins.rule) parts.push(`${origins.rule} por regra de cadastro`);
  return parts.join(", ");
}

/** A IA foi consultada pros temas restantes e não escolheu nenhum. Exige
 *  chamada que não falhou (chamada com erro não "respondeu nada") e, no
 *  rastro com origem, nenhum tema `ai`; no rastro antigo, sem origem, só
 *  dá pra afirmar quando não há tema nenhum. */
export function aiAnsweredNoTheme(
  themes: TraceThemes,
  calls: readonly { error?: string | null }[]
): boolean {
  if (calls.length === 0 || calls.some(call => call.error)) return false;
  return themes.origins ? themes.origins.ai === 0 : themes.names.length === 0;
}

export type TraceParticipation = { entity: string; percentage: number | null; text: string | null };

/** A participação por entidade do papel — texto em PT-BR da IA. */
export function traceParticipation(
  parsed: Record<string, unknown> | null | undefined
): TraceParticipation[] {
  return records(parsed?.participation).flatMap(item =>
    typeof item.entity === "string"
      ? [
          {
            entity: item.entity,
            percentage: typeof item.percentage === "number" ? item.percentage : null,
            text: typeof item.justification === "string" ? item.justification : null
          }
        ]
      : []
  );
}

export type TracePolarity = { rule: number | null; matchedTerms: string[] };

/** A premissa citada e os termos que casaram na polaridade. Rastro antigo
 *  não tem nenhum dos dois: devolve vazio. */
export function tracePolarity(parsed: Record<string, unknown> | null | undefined): TracePolarity {
  const rule = typeof parsed?.rule === "number" ? parsed.rule : null;
  const matched = Array.isArray(parsed?.matched_terms) ? parsed.matched_terms : [];
  return {
    rule,
    matchedTerms: matched.filter((term): term is string => typeof term === "string" && term !== "")
  };
}
